import type { RedirectActionProps } from '../components/actions/redirect';
import type { FailReasonValues, VerificationResultValues } from '@repo/common';
import type { ReactNode } from 'react';

import { FailReason } from '@repo/common';

import {
  ColorVariant,
  FailedIcon,
  NotFoundIcon,
  PrimaryButton,
  SecondaryButton,
  UnauthorizedIcon,
} from '../components';
import { RedirectAction } from '../components/actions/redirect';
import { useResultText } from '../hooks/use-result-text';
import { canResolveVerification, formatSubtitle } from '../utils';

import { VerificationFailedLayout } from './verification-failed-layout';

import { useAppContext } from '@/providers/app-provider/context';

function iconMap(failReason: FailReasonValues) {
  switch (failReason) {
    case FailReason.UNAUTHORIZED:
      return <UnauthorizedIcon />;

    case FailReason.SUSPENDED:
      return <UnauthorizedIcon />;

    case FailReason.NOT_FOUND:
      return <NotFoundIcon />;

    default:
      return <FailedIcon />;
  }
}

function titleColorMap(failReason: FailReasonValues) {
  switch (failReason) {
    case FailReason.NOT_FOUND:
    case FailReason.SESSION_EXPIRED:
    case FailReason.UNAUTHORIZED:
    case FailReason.SUSPENDED:
      return ColorVariant.INFO;

    default:
      return ColorVariant.ERROR;
  }
}

export type VerificationFailedProps = {
  errorMessage?: string;
  failReason: FailReasonValues;
  onRetry?: () => void;
  redirectPayload?: RedirectActionProps['payload'];
  redirectTimeoutInSec?: RedirectActionProps['timeoutInSec'];
  redirectUrl?: RedirectActionProps['url'];
  retryable?: boolean;
};

export const VerificationFailed = ({
  errorMessage,
  failReason,
  onRetry,
  redirectPayload = {},
  redirectTimeoutInSec,
  redirectUrl,
  retryable = false,
}: VerificationFailedProps) => {
  const { state } = useAppContext();
  const text = useResultText(failReason as VerificationResultValues);
  const retryLabel = text.retryButton ?? 'Try again';

  const renderActions = () => {
    const showRetryButton = retryable && !!onRetry;
    const showRedirectButton = canResolveVerification(redirectUrl, state.viewType) && !!text.redirectButton;

    const actions: ReactNode[] = [];

    if (showRedirectButton) {
      actions.push(
        <RedirectAction
          key="redirect-action"
          as={showRetryButton ? SecondaryButton : PrimaryButton}
          label={text.redirectButton!}
          payload={redirectPayload}
          timeoutInSec={redirectTimeoutInSec}
          url={redirectUrl ?? ''}
        />,
      );
    }

    if (showRetryButton) {
      actions.push(
        <PrimaryButton key="retry-action" onClick={onRetry}>
          {retryLabel}
        </PrimaryButton>,
      );
    }

    return actions;
  };

  return (
    <VerificationFailedLayout
      buttons={renderActions()}
      icon={iconMap(failReason)}
      infoText={state.verificationOnMobile ? text.mobileRedirectInfo : undefined}
      subtitle={formatSubtitle(text.subtitle, errorMessage)}
      title={text.title}
      titleColorVariant={titleColorMap(failReason)}
    />
  );
};
